import { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { useSafeQueryId } from "hooks/useSafeId";
import { useListMapQuery } from "hooks/worker/useMap";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { formatAs } from "utils/date";
import Layout from "component/templates/Layout";

import { getThisWeekRuns } from "./helper";
import { useEditModal } from "./useEditModal";
import { ScheduleEditModal } from "./ScheduleEditModal";
import { useDeleteModal } from "./useDeleteModal";
import { ScheduleDeleteModal } from "./ScheduleDeleteModal";
import { ScheduleCard } from "./ScheduleCard";
import { useCreateModal } from "./useCreateModal";
import { ScheduleCreateModal } from "./ScheduleCreateModal";
import {
  EXTERNAL_SERVICE_CODE,
  parseExternalServiceCodeType,
} from "types/externalService";
import { notify } from "lib/notify";

export const CleaningSchedulePage = () => {
  const robotId = useSafeQueryId("id");
  const { data: robots, isLoading, isFetched } = useListRobotQuery();
  const robot = robots?.find((r) => r.robotId === robotId);
  const { data: maps = [] } = useListMapQuery();
  const mapsBySrv = maps.filter((m) => {
    if (robot?.exSrvCode === EXTERNAL_SERVICE_CODE.TUYA) {
      return m.exSrvCode === EXTERNAL_SERVICE_CODE.TUYA;
    }
    const managedMapIds = robot?.mapIds ?? [];
    return managedMapIds.includes(m.mapId);
  });
  const schedules = robot?.schedules ?? [];
  const upcoming = getThisWeekRuns(schedules);

  const editModal = useEditModal({
    afterCallback: () => {
      notify("保存しました");
    },
  });
  const deleteModal = useDeleteModal({
    robotId: robot?.robotId,
    afterCallback: () => {
      notify("削除しました");
    },
  });
  const createModal = useCreateModal({
    afterCallback: () => {
      notify("追加しました");
    },
  });

  const parsedExSrvCode = parseExternalServiceCodeType(robot?.exSrvCode ?? "");

  useEffect(() => {
    if (isFetched && !robot) {
      throw new Error("ロボットの取得に失敗しました");
    }
  });

  if (!robot || isLoading) {
    return (
      <Layout title={`清掃スケジュール`}>
        <CircularProgress />
      </Layout>
    );
  }

  return (
    <Layout title={`${robot?.robotName} 清掃スケジュール`}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">今週のスケジュール</Typography>
          <Card variant="outlined">
            <CardContent>
              {upcoming.length === 0 ? (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  textAlign={"center"}
                >
                  <Typography>スケジュールはありません</Typography>
                </Box>
              ) : (
                upcoming.map((run, i) => {
                  const mapName =
                    mapsBySrv.find((m) => m.mapId === run.schedule.mapId)
                      ?.mapName ?? "不正なマップ";
                  return (
                    <Typography key={i} variant="body2">
                      {formatAs(run.datetime, "MM/dd (EEE) HH:mm")} {mapName}
                    </Typography>
                  );
                })
              )}
            </CardContent>
          </Card>
        </Box>
        <Box>
          <Box
            display="flex"
            justifyContent={"space-between"}
            alignItems={"center"}
            mb={1}
          >
            <Typography variant="h6">スケジュール一覧</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                if (mapsBySrv.length === 0) {
                  notify("登録できるマップがありません", "error");
                  return;
                }
                createModal.onOpen();
              }}
              variant="contained"
            >
              追加
            </Button>
          </Box>
          <Stack spacing={1}>
            {upcoming.length === 0 ? (
              <Card variant="outlined">
                <CardContent>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    textAlign={"center"}
                  >
                    <Typography gutterBottom>
                      スケジュールはありません
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (mapsBySrv.length === 0) {
                          notify("登録できるマップがありません", "error");
                          return;
                        }
                        createModal.onOpen();
                      }}
                      startIcon={<AddIcon />}
                    >
                      スケジュールを追加する
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              upcoming.map((run, idx) => {
                const mapName =
                  maps.find((m) => m.mapId === run.schedule.mapId)?.mapName ??
                  "不正なマップ";
                return (
                  <ScheduleCard
                    key={`schedule-${idx}`}
                    schedule={run.schedule}
                    mapName={mapName}
                    exSrvCode={robot.exSrvCode}
                    editAction={() => {
                      if (!parsedExSrvCode) {
                        notify("不正なコードです");
                        console.log({ code: robot.exSrvCode });
                        return;
                      }
                      editModal.onOpen(run.schedule);
                    }}
                    deleteAction={() => {
                      if (!parsedExSrvCode) {
                        notify("不正なコードです");
                        console.log({ code: robot.exSrvCode });
                        return;
                      }
                      deleteModal.onOpen(run.schedule, mapName);
                    }}
                  />
                );
              })
            )}
          </Stack>
        </Box>
      </Stack>
      {parsedExSrvCode && editModal.selectedSchedule && editModal.open && (
        <ScheduleEditModal
          open={editModal.open}
          onClose={editModal.onClose}
          schedule={editModal.selectedSchedule}
          schedules={schedules.filter(
            (s) => s.scheduleId !== editModal.selectedSchedule?.scheduleId
          )}
          exSrvCode={parsedExSrvCode}
          robotId={robot.robotId}
          maps={mapsBySrv}
          onUpdate={editModal.onUpdate}
          isUpdating={editModal.isUpdating}
        />
      )}
      {deleteModal.open && deleteModal.selectedSchedule && (
        <ScheduleDeleteModal
          open={deleteModal.open}
          onClose={deleteModal.onClose}
          schedule={deleteModal.selectedSchedule}
          mapName={deleteModal.mapName}
          onDelete={deleteModal.onDelete}
          isDeleting={deleteModal.isDeleting}
        />
      )}
      {parsedExSrvCode && createModal.open && (
        <ScheduleCreateModal
          exSrvCode={parsedExSrvCode}
          robotId={robot.robotId}
          maps={mapsBySrv}
          schedules={schedules}
          open={createModal.open}
          onClose={createModal.onClose}
          onCreate={createModal.onCreate}
          isCreating={createModal.isCreating}
        />
      )}
    </Layout>
  );
};
